import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Box, TextField, Button, MenuItem, Grid, Paper,
  Chip, IconButton, Snackbar, Alert, Switch, FormControlLabel,
  Tabs, Tab,
} from '@mui/material';
import MDEditor from '@uiw/react-md-editor';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageIcon from '@mui/icons-material/Image';
import {
  createArticle, updateArticle, getArticle, deleteArticle,
  listCategories, uploadMedia,
} from '../../utils/api';
import { Article, Category } from '../../utils/types';

const ArticleEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [videoUrl, setVideoUrl] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const load = async () => {
      const cats = await listCategories();
      setCategories(cats);

      if (!isNew && id) {
        try {
          const article = await getArticle(id);
          setTitle(article.title);
          setSlug(article.slug);
          setContent(article.content);
          setExcerpt(article.excerpt);
          setCategory(article.category);
          setTags(article.tags?.join(', ') || '');
          setStatus(article.status);
          setVideoUrl(article.videoUrl || '');
        } catch {
          setSnackbar({ open: true, message: 'Failed to load article', severity: 'error' });
        }
      }
    };
    load();
  }, [id, isNew]);

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (isNew) {
      setSlug(generateSlug(value));
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim() || !category) {
      setSnackbar({ open: true, message: 'Title, content, and category are required', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const articleData: Partial<Article> = {
        title,
        slug: slug || generateSlug(title),
        content,
        excerpt: excerpt || content.slice(0, 160).replace(/[#*_\n]/g, ''),
        category,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        status,
        videoUrl: videoUrl || undefined,
      };

      if (isNew) {
        await createArticle(articleData);
      } else if (id) {
        await updateArticle(id, articleData);
      }

      setSnackbar({ open: true, message: `Article ${isNew ? 'created' : 'updated'} successfully`, severity: 'success' });
      if (isNew) {
        setTimeout(() => navigate('/admin/articles'), 1000);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save article', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await deleteArticle(id);
      navigate('/admin/articles');
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete article', severity: 'error' });
    }
  };

  const getCursorPosition = (): number => {
    const textarea = document.querySelector('.w-md-editor-text-input') as HTMLTextAreaElement | null;
    return textarea?.selectionStart ?? content.length;
  };

  const insertAtCursor = (markdown: string, cursorPos: number) => {
    setContent(prev => {
      const pos = Math.min(cursorPos, prev.length);
      return prev.slice(0, pos) + `\n${markdown}\n` + prev.slice(pos);
    });
  };

  const insertImage = async (file: File, cursorPos?: number) => {
    const pos = cursorPos ?? getCursorPosition();
    try {
      setSnackbar({ open: true, message: 'Uploading image...', severity: 'success' });
      const media = await uploadMedia(file, 'images');
      insertAtCursor(`![${file.name}](${media.url})`, pos);
      setSnackbar({ open: true, message: 'Image uploaded and inserted', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to upload image', severity: 'error' });
    }
  };

  const handleImageUpload = () => {
    const pos = getCursorPosition();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) await insertImage(file, pos);
    };
    input.click();
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pos = getCursorPosition();
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) await insertImage(file, pos);
        return;
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/admin/articles')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            {isNew ? 'New Article' : 'Edit Article'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isNew && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
              Delete
            </Button>
          )}
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #E0E0E0' }}>
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              size="small"
              sx={{ mb: 3 }}
              helperText="URL-friendly identifier"
            />

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: 36 }}>
                <Tab label="Write" sx={{ minHeight: 36, py: 0 }} />
                <Tab label="Preview" sx={{ minHeight: 36, py: 0 }} />
              </Tabs>
              <IconButton size="small" onClick={handleImageUpload} title="Insert Image">
                <ImageIcon />
              </IconButton>
            </Box>

            <Box data-color-mode="light" onPaste={handlePaste}>
              <MDEditor
                value={content}
                onChange={(val) => setContent(val || '')}
                height={500}
                preview={activeTab === 1 ? 'preview' : 'edit'}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #E0E0E0', mb: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Settings</Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={status === 'published'}
                  onChange={(e) => setStatus(e.target.checked ? 'published' : 'draft')}
                />
              }
              label={status === 'published' ? 'Published' : 'Draft'}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ mb: 2 }}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.slug}>{cat.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              helperText="Comma-separated"
            />

            <TextField
              fullWidth
              label="Excerpt"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              multiline
              rows={3}
              size="small"
              helperText="Short summary shown in article cards"
            />
          </Paper>

          <Paper elevation={0} sx={{ p: 3, border: '1px solid #E0E0E0' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Video</Typography>
            <TextField
              fullWidth
              label="Video URL"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              size="small"
              helperText="S3 URL or external video link"
            />
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ArticleEditor;
